import { ChangeDetectorRef, Component, inject, OnInit } from "@angular/core";
import { FieldType } from "@ngx-formly/core";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { V2ConfirmationService } from "@vui";
import { TranslocoService } from "@jsverse/transloco";

export function dateNotInFuture(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const dateValue = control.value;

        if (!dateValue) {
            return null;
        }

        const inputDate = new Date(dateValue);
        const today = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        return inputDate <= today
            ? null
            : { dateNotInFuture: true };
    };
}

interface TableColumn {
    id: string;
    date: any;
}

interface TableRow {
    __id: string;
    __label: string | null;
    __locked?: boolean;
    values: Record<string, any>;
}

interface TableModel {
    columns: TableColumn[];
    rows: TableRow[];
}

@Component({
    selector: "formly-field-table",
    styleUrls: ["./table.type.scss"],
    templateUrl: "./table.type.html",
    providers: [V2ConfirmationService]
})
export class FormlyFieldTable extends FieldType implements OnInit {
    private _cdr = inject(ChangeDetectorRef);

    private _v2ConfirmationService = inject(V2ConfirmationService);

    private t = inject(TranslocoService);

    tableModel!: TableModel;

    tableForm = new FormGroup({});

    isHydrating = false;

    ngOnInit(): void {
        if (!this.formControl) {
            throw new Error("Formly control not initialized");
        }

        this.isHydrating = true;

        this.tableModel =
            structuredClone(this.formControl.value)
            ?? structuredClone(this.field.defaultValue)
            ?? {
                columns: [],
                rows: [],
            };

        this.tableModel.columns ??= [];
        this.tableModel.rows ??= [];

        if (!this.tableModel.columns.length) {
            this.tableModel.columns.push({ id: "col_initial", date: null });
        }

        if (!this.tableModel.rows.length && this.to.tableRows?.length) {
            this.to.tableRows.forEach((label: string) => {
                this.tableModel.rows.push({
                    __id: crypto.randomUUID(),
                    __label: label,
                    __locked: true,
                    values: {},
                });
            });
        }

        this.normalizeRows();
        this.normalizeColumns();

        this.createColumnDateControls();
        this.syncCellControls();

        this.formControl.setValue(structuredClone(this.tableModel));

        this.isHydrating = false;
        this.tableForm.valueChanges.subscribe(() => this.syncToFormly());
    }

    private normalizeRows(): void {
        for (const row of this.tableModel.rows) {
            row.__id ??= crypto.randomUUID();
            row.values ??= {};
        }
    }

    private normalizeColumns(): void {
        for (const col of this.tableModel.columns) {
            col.id ??= crypto.randomUUID();

            if (typeof col.date === 'string') {
                col.date = new Date(col.date);
            }
        }
    }

    private syncToFormly(): void {
        if (this.isHydrating) return;

        this.syncColumnDatesToModel();
        this.syncCellsToModel();
        this.syncRowLabelsToModel();

        this.formControl.setValue(structuredClone(this.tableModel));
    }

    private syncCellsToModel(): void {
        for (const row of this.tableModel.rows) {
            for (const col of this.tableModel.columns) {
                const ctrl = this.tableForm.get(this.controlKey(row, col));
                if (ctrl) {
                    row.values[col.id] = ctrl.value ?? null;
                }
            }
        }
    }

    private syncRowLabelsToModel(): void {
        for (const row of this.tableModel.rows) {
            const ctrl = this.tableForm.get(this.rowLabelKey(row));
            if (ctrl) {
                row.__label = ctrl.value ?? null;
            }
        }
    }

    private syncColumnDatesToModel(): void {
        for (const col of this.tableModel.columns) {
            const ctrl = this.tableForm.get(this.columnDateKey(col));
            if (!ctrl) continue;

            const value = ctrl.value;
            col.date = value instanceof Date
                ? value.toISOString()
                : value ?? null;
        }
    }

    private createColumnDateControls(): void {
        for (const col of this.tableModel.columns) {

            const key = this.columnDateKey(col);
            if (!this.tableForm.contains(key)) {
                this.tableForm.addControl(
                    key,
                    new FormControl(col.date, {
                        validators: [dateNotInFuture()],
                    })
                );
            } else {
                this.tableForm.get(key)?.setValue(col.date);
            }
        }

    }

    private syncCellControls(): void {
        for (const row of this.tableModel.rows) {

            const labelKey = this.rowLabelKey(row);
            if (!this.tableForm.contains(labelKey)) {
                this.tableForm.addControl(
                    labelKey,
                    new FormControl(row.__label)
                );
            } else {
                this.tableForm.get(labelKey)?.setValue(row.__label);
            }

            for (const col of this.tableModel.columns) {
                const key = this.controlKey(row, col);
                const value = row.values[col.id] ?? null;

                if (!this.tableForm.contains(key)) {
                    this.tableForm.addControl(key, new FormControl(value));
                } else {
                    this.tableForm.get(key)?.setValue(value);
                }
            }
        }
    }

    columnDateKey(col: TableColumn): string {
        return `__column_date__${col.id}`;
    }

    rowLabelKey(row: TableRow): string {
        return `__row_label__${row.__id}`;
    }

    rowLabelControl(row: TableRow): FormControl {
        return this.tableForm.get(this.rowLabelKey(row)) as FormControl;
    }

    getColumnDateControl(col: TableColumn): FormControl {
        return this.tableForm.get(this.columnDateKey(col)) as FormControl;
    }

    controlKey(row: TableRow, col: TableColumn): string {
        return `${row.__id}__${col.id}`;
    }

    getCellControl(row: TableRow, col: TableColumn): FormControl {
        return this.tableForm.get(this.controlKey(row, col)) as FormControl;
    }

    addRow(): void {
        const row: TableRow = {
            __id: crypto.randomUUID(),
            __label: null,
            __locked: false,
            values: {},
        };

        this.tableModel.rows.push(row);
        this.syncCellControls();
        this.syncToFormly();
    }

    removeRow(index: number): void {
        this._v2ConfirmationService
            .openV2({
                title: this.t.translate("formly-table.removeRow.title"),
                message: this.t.translate("formly-table.removeRow.message"),
                actions: {
                    confirm: {
                        show: true,
                        label: this.t.translate("actions.yes"),
                        color: "warn",
                    },
                    discard: {
                        show: false,
                    },
                    cancel: {
                        show: true,
                        label: this.t.translate("actions.no"),
                    },
                },
                dismissible: false,
            })
            .afterClosed()
            .subscribe((result) => {
                if (result === "confirmed") {
                    const row = this.tableModel.rows[index];
                    if (row?.__locked) return;

                    this.tableModel.columns.forEach(col => {
                        this.tableForm.removeControl(this.controlKey(row, col));
                    });

                    this.tableModel.rows.splice(index, 1);

                    this.syncToFormly();

                    this._cdr.markForCheck();
                }
            });

    }

    addColumn(): void {
        const col: TableColumn = {
            id: `col_${crypto.randomUUID()}`,
            date: null,
        };

        this.tableModel.columns.push(col);

        this.createColumnDateControls();
        this.syncCellControls();
        this.syncToFormly();
    }

    removeColumn(col: TableColumn): void {
        this._v2ConfirmationService
            .openV2({
                title: this.t.translate("formly-table.removeColumn.title"),
                message: this.t.translate("formly-table.removeColumn.message"),
                actions: {
                    confirm: {
                        show: true,
                        label: this.t.translate("actions.yes"),
                        color: "warn",
                    },
                    discard: {
                        show: false,
                    },
                    cancel: {
                        show: true,
                        label: this.t.translate("actions.no"),
                    },
                },
                dismissible: false,
            })
            .afterClosed()
            .subscribe((result) => {
                if (result === 'confirmed') {
                    const idx = this.tableModel.columns.findIndex(c => c.id === col.id);
                    if (idx === -1) return;

                    this.tableModel.rows.forEach(row => {
                        this.tableForm.removeControl(this.controlKey(row, col));
                        delete row.values[col.id];
                    });

                    this.tableForm.removeControl(this.columnDateKey(col));

                    this.tableModel.columns.splice(idx, 1);

                    this.syncToFormly();

                    this._cdr.markForCheck();
                }
            });
    }
}
